import { v4 as uuidv4 } from 'uuid';

/**
 * An opaque id identifying THIS browser tab, sent on every API request as
 * `x-client-tab-id` and recorded on the backend request log.
 *
 * It exists to answer one question the access log could not: when a user
 * appears to re-read the same handful of artifacts hundreds of times, is that
 * ONE tab in a refetch loop, or several tabs each fetching normally? The log
 * carries no session, tab, user-agent or IP field, so both look identical.
 *
 * `sessionStorage`, deliberately:
 *   - it is per-TAB (localStorage is shared across tabs, which would defeat
 *     the entire purpose), and
 *   - it survives a reload, so a loop that spans reloads stays one id.
 *
 * Not PII: a random id, scoped to the tab, never sent anywhere but our own API.
 * Access is wrapped because `sessionStorage` THROWS in Safari private mode and
 * in sandboxed iframes — a diagnostic must never break the request path.
 */

const STORAGE_KEY = 'bower.clientTabId';

let cached: string | undefined;

export function getClientTabId(): string | undefined {
  if (cached) return cached;

  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      cached = existing;
      return cached;
    }
    const minted = uuidv4();
    sessionStorage.setItem(STORAGE_KEY, minted);
    cached = minted;
    return cached;
  } catch {
    // Storage unavailable. Fall back to a per-page-load id held in memory: it
    // no longer survives a reload, but it still distinguishes concurrent tabs,
    // which is the question being asked.
    cached = uuidv4();
    return cached;
  }
}

/** @internal Test seam: drop the memoised value so each test mints its own. */
export function __resetClientTabIdForTests(): void {
  cached = undefined;
}
